import { BehaviorSubject, map, ReplaySubject, Subject, takeUntil } from 'rxjs';
import Pubnub from 'pubnub';
import { Injectable, OnDestroy } from '@angular/core';
import { cacheFactory } from 'src/util/cache';
import { MessageReceived } from 'src/store/pubnub/pubnub-store.actions';
import { shareReplaySafe } from 'src/util/rxjs/operators/share-replay-safe';
import { PubnubStatusEvent } from './pubnub-types';

export enum SubscriptionStatus {
  None = 'None',
  Subscribing = 'Subscribing',
  Subscribed = 'Subscribed',
  Unsubscribed = 'Unsubscribed',
  Failed = 'Failed',
}

export interface PubnubMessageEntityMeta {
  type: string;
  source: string;
  received: Date;
}

export interface PubnubMessageMeta {
  type: string;
  entityCount: number;
}

interface PubnubChannelGroup {
  name: string;
  channels: (PubnubChannelStatus | PubnubChannelRemoved)[];
  changed: boolean;
  hrTime: number;
}

export interface PubnubChannelStatus {
  channel: string;
  lastMessageReceived: Date | null;
  lastMessageMetadata: PubnubMessageMeta | null;
  subscriptionStatus: SubscriptionStatus;
  lastError: PubnubStatusEvent | null;
  hasError: boolean;
  hrTime: number;
  changed: boolean;
}

interface PubnubChannelRemoved extends PubnubChannelStatus {
  removed: true;
  acknowledge: () => void;
}

const connectedEvents = [
  Pubnub.CATEGORIES.PNConnectedCategory,
  Pubnub.CATEGORIES.PNReconnectedCategory,
];
const disconnectedEvents = [
  Pubnub.CATEGORIES.PNNetworkIssuesCategory,
  Pubnub.CATEGORIES.PNDisconnectedCategory,
  Pubnub.CATEGORIES.PNNetworkDownCategory,
  Pubnub.CATEGORIES.PNDisconnectedUnexpectedlyCategory,
];
const errorEvents = [
  Pubnub.CATEGORIES.PNNetworkIssuesCategory,
  Pubnub.CATEGORIES.PNNetworkDownCategory,
  Pubnub.CATEGORIES.PNDisconnectedUnexpectedlyCategory,
  Pubnub.CATEGORIES.PNBadRequestCategory,
  Pubnub.CATEGORIES.PNAccessDeniedCategory,
  Pubnub.CATEGORIES.PNServerErrorCategory,
  Pubnub.CATEGORIES.PNConnectionErrorCategory,
  Pubnub.CATEGORIES.PNMalformedResponseCategory,
  Pubnub.CATEGORIES.PNUnknownCategory,
];

class PubnubStatusMonitor {
  private _channels$ = new BehaviorSubject<Map<string, PubnubChannelGroup>>(new Map());
  private _lastMessageReceived$ = new ReplaySubject<Date>(1);
  private _lastError$ = new ReplaySubject<PubnubStatusEvent>(1);
  private _hasError$ = new BehaviorSubject<boolean>(false);
  private _connected$ = new BehaviorSubject<boolean>(false);
  private _channelUpdates = cacheFactory.accessTracker();
  private _groupUpdates = cacheFactory.accessTracker();

  public readonly connected$ = this._connected$.asObservable();
  public readonly lastMessageReceived$ = this._lastMessageReceived$.asObservable();
  public readonly lastError$ = this._lastError$.asObservable();
  public readonly hasError$ = this._hasError$.asObservable();
  public readonly channels$ = this._channels$.pipe(
    map((groups) => {
      groups.forEach((group) => {
        const lastGroupChanged = this._groupUpdates.get(group.name).hrTime;
        const isGroupChanged = lastGroupChanged == null || lastGroupChanged < group.hrTime;
        group.changed = isGroupChanged;
        group.channels.forEach((status, idx) => {
          const lastStatusChanged = this._channelUpdates.get(status.channel).hrTime;
          const isStatusChanged = lastStatusChanged == null || lastStatusChanged < status.hrTime;
          status.changed = isStatusChanged;
        });
      });

      return groups as ReadonlyMap<string, Readonly<PubnubChannelGroup>>;
    }),
    shareReplaySafe(1),
  );

  public recordMessageReceived(message: MessageReceived, date?: Date) {
    const msg = message.message;
    const channel = msg.channel;
    const messageReceivedDate = date ?? new Date();
    this._lastMessageReceived$.next(messageReceivedDate);
    this._updateChannel(channel, {
      lastMessageReceived: messageReceivedDate,
      lastMessageMetadata: {
        type: msg.message.type,
        entityCount: (msg.message.body as any)?.entities?.length ?? 0,
      },
    });
    if (this._hasError$.value) {
      // message came in so clear the error state
      this._hasError$.next(false);
    }
  }

  public updateSubscribes(
    subscribingChannels: string[],
    subscribedChannels: string[],
    failed?: true,
  ) {
    const subscribing = subscribingChannels.filter((c) => !subscribedChannels.includes(c));
    if (subscribing.length > 0) {
      subscribing.forEach((channel) => {
        this._setSubscribing(channel, failed);
      });
    }
    if (subscribing.length !== subscribingChannels.length) {
      const subscribed = subscribedChannels.filter((c) => subscribingChannels.includes(c));
      subscribed.forEach((channel) => {
        this._setSubscribed(channel);
      });
    }
  }

  public removeChannels(channels: string[]) {
    channels.forEach((channel) => {
      this._removeChannel(channel);
    });
  }

  public updateStatus(status: PubnubStatusEvent) {
    if (connectedEvents.includes(status.category)) {
      if (!this._connected$.value) {
        this._connected$.next(true);
      }
    } else if (disconnectedEvents.includes(status.category)) {
      if (this._connected$.value) {
        this._connected$.next(false);
      }
    }

    if (errorEvents.includes(status.category)) {
      const channels = status.affectedChannels ?? [];
      if (channels.length > 0) {
        channels.forEach((channel) => {
          this._lastError$.next(status);
          this._hasError$.next(true);
          this._updateChannel(channel, { lastError: status, hasError: true });
        });
      } else {
        this._lastError$.next(status);
        this._hasError$.next(true);
      }
    } else {
      this._hasError$.next(false);
    }

    if (status.operation === Pubnub.OPERATIONS.PNUnsubscribeOperation) {
      const channels = status.affectedChannels ?? [];
      channels.forEach((channel) => {
        this._removeChannel(channel);
      });
    }
  }

  private _setSubscribing(channelName: string, failed?: true) {
    this._updateChannel(channelName, {
      subscriptionStatus: failed ? SubscriptionStatus.Failed : SubscriptionStatus.Subscribing,
    });
  }

  private _setSubscribed(channelName: string) {
    this._updateChannel(channelName, {
      subscriptionStatus: SubscriptionStatus.Subscribed,
      lastError: null,
    });
  }

  private _getOrAddGroupChannel(channelName: string): PubnubChannelGroup {
    const groupName = this._getChannelGroupName(channelName);
    const groups = this._channels$.value;
    let group = groups.get(groupName);
    if (group == null) {
      group = {
        name: groupName,
        channels: [],
        changed: true,
        hrTime: performance.now(),
      };
      groups.set(groupName, group);
    }
    let channelStatus = group.channels.find((c) => channelName.match(c.channel));
    if (channelStatus == null) {
      const status: PubnubChannelStatus = {
        channel: channelName,
        lastMessageReceived: null,
        lastMessageMetadata: null,
        subscriptionStatus: SubscriptionStatus.None,
        lastError: null,
        hasError: false,
        hrTime: performance.now(),
        changed: true,
      };
      channelStatus = status;
      group.channels.push(status);
    }
    return group;
  }

  private _removeChannel(channelName: string) {
    const groupName = this._getChannelGroupName(channelName);
    const groups = this._channels$.value;
    const group = groups.get(groupName);
    if (group != null) {
      const statusIndex = group.channels.findIndex((c) => channelName.match(c.channel));
      if (statusIndex !== -1) {
        const status = group.channels[statusIndex];
        const removedStatus: PubnubChannelRemoved = {
          ...status,
          subscriptionStatus: SubscriptionStatus.Unsubscribed,
          hrTime: performance.now(),
          removed: true,
          acknowledge: () => {
            const index = group.channels.findIndex((c) => c === removedStatus);
            if (index !== -1) {
              group.channels.splice(index, 1);
              if (group.channels.length === 0) {
                groups.delete(groupName);
                this._groupUpdates.delete(groupName);
              }
              this._channelUpdates.delete(channelName);
              this._channels$.next(groups);
            }
          },
        };
        group.channels[statusIndex] = removedStatus;
        group.hrTime = performance.now();
      }
      this._channels$.next(groups);
    }
  }

  private _updateChannel(channelName: string, update: Partial<PubnubChannelStatus>) {
    const group = this._getOrAddGroupChannel(channelName);
    const status = group.channels.find((c) => channelName.match(c.channel));
    if (status == null) {
      return;
    }
    const nextStatus: PubnubChannelStatus = {
      ...status,
      hasError: false,
      ...update,
      hrTime: performance.now(),
    };
    const index = group.channels.findIndex((c) => channelName.match(c.channel));
    if (index !== -1) {
      group.channels[index] = nextStatus;
      group.hrTime = performance.now();
    }
    this._channels$.next(this._channels$.value);
  }

  private _getChannelGroupName(channelName: string): string {
    // remove everything after first dot to track base channel.
    return channelName.replace(/\..*/, '');
  }
}

export const pubnubMonitor = new PubnubStatusMonitor();

export interface PubnubChannel extends PubnubChannelStatus {
  groupName: string;
  groupChanged: boolean;
}

interface PubnubChannelData {
  action: 'add' | 'remove';
  channel: PubnubChannel;
}

@Injectable({
  providedIn: 'root',
})
export class PubnubMonitorService implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _subscribingChannels$ = new BehaviorSubject<PubnubChannel[]>([]);
  private _subscribedChannels$ = new BehaviorSubject<PubnubChannel[]>([]);
  private _unsubscribedChannels$ = new BehaviorSubject<PubnubChannel[]>([]);
  private _subscribeFailedChannels$ = new BehaviorSubject<PubnubChannel[]>([]);

  public readonly subscribingChannels$ = this._subscribingChannels$.asObservable();
  public readonly subscribedChannels$ = this._subscribedChannels$.asObservable();
  public readonly unsubscribedChannels$ = this._unsubscribedChannels$.asObservable();
  public readonly subscribeFailedChannels$ = this._subscribeFailedChannels$.asObservable();

  public readonly connected$ = pubnubMonitor.connected$;
  public readonly lastMessageReceived$ = pubnubMonitor.lastMessageReceived$;
  public readonly lastError$ = pubnubMonitor.lastError$;
  public readonly hasError$ = pubnubMonitor.hasError$;

  constructor() {
    pubnubMonitor.channels$.pipe(takeUntil(this._onDestroy$)).subscribe((groups) => {
      const subscribing: PubnubChannelData[] = [];
      const subscribed: PubnubChannelData[] = [];
      const unsubscribed: PubnubChannelData[] = [];
      const failedSubscribes: PubnubChannelData[] = [];

      groups.forEach((group) => {
        group.channels.forEach((status) => {
          if (status.subscriptionStatus === SubscriptionStatus.Subscribing) {
            this._add(subscribing, group, status);
            subscribed.push(...this._remove(this._subscribedChannels$, status.channel));
            unsubscribed.push(...this._remove(this._unsubscribedChannels$, status.channel));
            failedSubscribes.push(...this._remove(this._subscribeFailedChannels$, status.channel));
          } else if (status.subscriptionStatus === SubscriptionStatus.Subscribed) {
            this._add(subscribed, group, status);
            subscribing.push(...this._remove(this._subscribingChannels$, status.channel));
            unsubscribed.push(...this._remove(this._unsubscribedChannels$, status.channel));
            failedSubscribes.push(...this._remove(this._subscribeFailedChannels$, status.channel));
          } else if (status.subscriptionStatus === SubscriptionStatus.Failed) {
            this._add(failedSubscribes, group, status);
            subscribing.push(...this._remove(this._subscribingChannels$, status.channel));
            subscribed.push(...this._remove(this._subscribedChannels$, status.channel));
            unsubscribed.push(...this._remove(this._unsubscribedChannels$, status.channel));
          } else if (status.subscriptionStatus === SubscriptionStatus.Unsubscribed) {
            if ('acknowledge' in status) {
              const { removed, acknowledge, ...channelStatus } = status;
              this._add(unsubscribed, group, channelStatus);
              subscribing.push(...this._remove(this._subscribingChannels$, status.channel));
              subscribed.push(...this._remove(this._subscribedChannels$, status.channel));
              failedSubscribes.push(
                ...this._remove(this._subscribeFailedChannels$, status.channel),
              );
              acknowledge();
            }
          }
        });
      });

      if (subscribing.some((c) => c.channel.changed || c.action === 'remove')) {
        const subs = subscribing.filter((c) => c.action !== 'remove').map((c) => c.channel);
        this._subscribingChannels$.next(subs);
      }
      if (subscribed.some((c) => c.channel.changed || c.action === 'remove')) {
        const subed = subscribed.filter((c) => c.action !== 'remove').map((c) => c.channel);
        this._subscribedChannels$.next(subed);
      }
      if (unsubscribed.some((c) => c.channel.changed || c.action === 'remove')) {
        const unsubed = unsubscribed.filter((c) => c.action !== 'remove').map((c) => c.channel);
        this._unsubscribedChannels$.next(unsubed);
      }
      if (failedSubscribes.some((c) => c.channel.changed || c.action === 'remove')) {
        const failed = failedSubscribes.filter((c) => c.action !== 'remove').map((c) => c.channel);
        this._subscribeFailedChannels$.next(failed);
      }
    });
  }

  private _add(
    source: PubnubChannelData[],
    group: PubnubChannelGroup,
    status: PubnubChannelStatus,
  ) {
    const groupName = group.name;
    const groupChanged = group.changed;
    const channel: PubnubChannel = { groupName, groupChanged, ...status };
    source.push({ action: 'add', channel });
  }

  private _remove(source: BehaviorSubject<PubnubChannel[]>, channel: string): PubnubChannelData[] {
    const index = source.value.findIndex((c) => c.channel === channel);
    return index === -1
      ? []
      : source.value.splice(index, 1).map((c) => {
          const item: PubnubChannelData = { action: 'remove', channel: c };
          return item;
        });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
